import React from 'react';
import { createSelector } from 'reselect';
import get from 'lodash/get';
import { humanize } from '@utils';
import { INVERTED_METRIC_SWITCH_MAP, LESS_THAN_NUMBER } from '@utils/metrics';
import { LIVE_EXCLUDE_METRICS } from '@utils/metricLens/mapping';
import { historicalRangeTableDataSortSelector } from '@store/metricLens/historicalRangeData/selectors';
import { liveRangeDataSelector } from '@store/metricLens/liveData/selectors';
import {
  selectedMetricsSelector,
  appliedSortSelector,
  selectedDimensionSelector,
  unitModeSelector,
  liveModeSelector,
  appliedSearchSelector,
} from '@store/metricLens/base/selectors';
import { getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import Header from './Header';
import Cell from './Cell';
import RowHeader from './RowHeader';
import DimensionHeader from './DimensionHeader';

// main column (left, frozen)
const mainColSelector: any = createSelector(selectedDimensionSelector, (dimension) => {
  const columns = [
    {
      title: dimension,
      dataKey: 'name',
      key: dimension,
      width: 300,
      frozen: true,
      resizable: true,
      headerRenderer: () => <DimensionHeader />,
      cellRenderer: ({ cellData, rowData }) => <RowHeader text={cellData} rowData={rowData} />,
      align: 'left',
    },
  ];
  return columns;
});

// remaining metric based columns
export const metricColumnSelector = createSelector(
  selectedMetricsSelector,
  unitModeSelector,
  liveModeSelector,
  (metricList, unit, liveMode) => {
    if (liveMode) {
      const excludeList = LIVE_EXCLUDE_METRICS;
      excludeList.forEach((exclude) => {
        if (metricList.includes(exclude)) {
          metricList.splice(metricList.indexOf(exclude), 1);
        }
      });
    }
    const withInvertedMetrics = metricList.map((m) => {
      if (unit === '%' && m in INVERTED_METRIC_SWITCH_MAP) {
        return INVERTED_METRIC_SWITCH_MAP[m];
      }
      return m;
    });
    const tableColumns = withInvertedMetrics.map((metric) => {
      const metricInfo = getMetricMetaByKey({ metricKey: metric });
      return {
        title: metricInfo.short,
        dataKey: metric,
        key: metric,
        width: 300,
        headerRenderer: () => <Header metric={metricInfo} />,
        cellRenderer: ({ cellData, column, rowData, ...rest }) => (
          <Cell
            cellData={cellData}
            column={column}
            rowData={rowData}
            unit={metricInfo.unit}
            {...rest}
          />
        ),
        align: 'left',
      };
    });
    return tableColumns;
  },
);

// combined columns
export const columnsSelector = createSelector(
  mainColSelector,
  metricColumnSelector,
  (mainColumns, metricColumns) => [...mainColumns, ...metricColumns],
);

// wrapper around humanize to handle display requirments
export const humanizeValue = (number: number, unit: string) => {
  // for very small numbers
  if (number && number < LESS_THAN_NUMBER) {
    return { humanizedNumber: '< 0.001', humanizedUnit: unit };
  }

  // humanize
  const humanizedValue = humanize(number, unit);
  let data = {
    humanizedNumber: humanizedValue,
    humanizedUnit: unit,
  };

  // split prefix unit if any
  try {
    if (isNaN(humanizedValue)) {
      // eslint-disable-next-line no-useless-escape
      const splitValue = humanizedValue.toString().match(/([\d\.]+)|(\D+)/g);
      if (splitValue) {
        data = {
          humanizedNumber: splitValue[0] || humanizedValue,
          humanizedUnit: `${splitValue[1] || ''}${unit}`,
        };
      }
    }
  } catch (err) {
    // defaults to original humanized value
  }

  return data;
};

export const historicalRangeDataSelector = createSelector(
  historicalRangeTableDataSortSelector,
  liveRangeDataSelector,
  selectedMetricsSelector,
  appliedSortSelector,
  liveModeSelector,
  appliedSearchSelector,
  (historicalData, liveRangeData, metrics, sort, liveMode, appliedSearch) => {
    const expectedData = liveMode ? liveRangeData : historicalData;
    let sortedTableList = expectedData[sort.filter];
    if (sortedTableList) {
      if (appliedSearch !== '') {
        sortedTableList = sortedTableList.filter((row) => {
          const name = get(row, ['name', 0]);
          if (name.toLowerCase().includes(appliedSearch.toLowerCase())) {
            return true;
          }
          return false;
        });
      }
      const tableData: Array<{ name: string; id: string; value: string }> = [];
      const invertedMetrics: string[] = [];
      metrics.forEach((m) => {
        if (m in INVERTED_METRIC_SWITCH_MAP) {
          invertedMetrics.push(INVERTED_METRIC_SWITCH_MAP[m]);
        }
      });
      const fullMetrics = [...metrics, ...invertedMetrics];
      sortedTableList.forEach((filterData) => {
        const rowData = {
          name: filterData.name,
          id: filterData.id,
          value: filterData.value,
        };
        fullMetrics.forEach((metric) => {
          rowData[metric] = [];
        });
        expectedData.forEach((eachData) => {
          const data = eachData.find((obj) => obj.id === filterData.id);
          fullMetrics.forEach((metric) => {
            rowData[metric].push(data[metric]);
          });
        });
        tableData.push(rowData);
      });
      return tableData;
    }
    return [];
  },
);
